import deepEqual from 'fast-deep-equal'

import { doAppGraphqlRequest } from 'ducks/requests'
import {
  doUpdateCustomFieldFileValues,
  selectCustomFieldForKey,
} from 'ducks/crm/customFields'
import { all } from 'util/arrays'
import { readAsDataURL } from 'util/file'
import { selectIsInChat } from 'selectors/location'

import {
  changeContactMutationSchema,
  createContactMutationSchema,
  customFieldValueCompanyNormalizationScheme,
  customFieldValueContactNormalizationScheme,
} from '../schema'
import { selectCustomFieldValueForCustomFieldKeyAndSubjectId } from '../selectors'
import {
  CHANGE_CONVERSATION_CONTACT,
  CREATE_CONTACT,
  UPDATE_CUSTOM_FIELD_VALUE,
} from '../types'
import { customFieldValueNodeQuery } from '../../customFields/operations/fetching'
import { doFetchCurrentContact } from './fetching'

const updateCustomFieldValueQuery = `
mutation UpdateCustomFieldValue(
  $input: CustomFieldValuesUpdateInput!,
  $customFieldIds: [ID!]
) {
  customFieldValuesUpdate(input: $input) {
    subject {
      ... on Node {
        id
      }
      ... on CustomFieldValuesField {
        customFieldValues(filter: { customFieldIds: $customFieldIds }) {
          ${customFieldValueNodeQuery}
        }
      }
    }

    errors {
      message
      path
      type
    }
  }
}
`

export function doUpdateCustomFieldValues(subjectId, fields) {
  return async dispatch => {
    const fileFields = fields.filter(
      ({ isFileField, remove }) => isFileField && !remove
    )
    const nonFileFields = fields.filter(
      ({ isFileField, remove }) => !isFileField || remove
    )
    const isSubjectCompany = subjectId.match(/^cm_/)
    const subjectKey = isSubjectCompany ? 'companies' : 'contacts'
    const fileFieldsWithDataUrls = await Promise.all(
      fileFields.map(field => {
        return readAsDataURL(field.value).then(dataUrl => ({
          ...field,
          dataUrl,
        }))
      })
    )
    const optimist = {
      entities: {
        [subjectKey]: {
          [subjectId]: {
            id: subjectId,
            customFieldValues: {
              nodes: [
                ...nonFileFields.map(
                  ({ key, isArray, mode, value, valueId }) => ({
                    id: valueId,
                    customField: { isArray },
                    key,
                    value,
                    mode,
                  })
                ),
                ...fileFieldsWithDataUrls.map(({ dataUrl, key, valueId }) => ({
                  id: valueId,
                  key,
                  value: { dataUrl },
                })),
              ],
            },
          },
        },
      },
    }
    const attachments = await (fileFields.length > 0
      ? dispatch(
          doUpdateCustomFieldFileValues(fileFields, {
            optimist,
            meta: {
              mergeEntities: true,
            },
          })
        )
      : Promise.resolve([]))
    const customFieldList = [
      ...nonFileFields.map(({ id, mode, value }) => {
        const newValue =
          value && deepEqual(Object.keys(value), ['content'])
            ? value.content
            : value
        return {
          customFieldIdOrKey: id,
          ...(newValue === '' || !newValue
            ? { remove: true }
            : { value: newValue, mode }),
        }
      }),
      ...attachments.map(({ id, value }) => {
        return {
          customFieldIdOrKey: id,
          value: {
            content_type: value.attachment_content_type,
            file_name: value.attachment_file_name,
            file_size: value.attachment_file_size,
            attachment_id: value.id,
          },
        }
      }),
    ]
    if (customFieldList.length === 0) return null
    const variables = {
      input: {
        subjectId,
        customFieldList,
      },
      customFieldIds: fields.map(({ id }) => id),
    }
    return dispatch(
      doAppGraphqlRequest(
        UPDATE_CUSTOM_FIELD_VALUE,
        updateCustomFieldValueQuery,
        variables,
        {
          optimist,
          meta: {
            mergeEntities: true,
            attachments,
          },
          normalizationSchema: isSubjectCompany
            ? customFieldValueCompanyNormalizationScheme
            : customFieldValueContactNormalizationScheme,
          transformEntities: entitities => {
            const subjects = entitities[subjectKey] || {}
            Object.keys(subjects).forEach(id => {
              // eslint-disable-next-line no-param-reassign
              subjects[id].customFieldValues =
                subjects[id].customFieldValues || {}
            })
            return entitities
          },
        }
      )
    )
  }
}

function valueIsEmpty(value) {
  return (
    !value || all(x => ['', undefined, null].includes(x), Object.values(value))
  )
}

export function doUpdateCustomFieldValuesFromForm(subjectId, formData) {
  return (dispatch, getState) => {
    const state = getState()
    const doneKeys = []

    const newValues = Object.keys(formData)
      .reduce((result, key) => {
        const { id: valueId, value: existingValue } =
          selectCustomFieldValueForCustomFieldKeyAndSubjectId(state, {
            customFieldKey: key,
            subjectId,
          }) || {}
        const { id, isArray, type } =
          selectCustomFieldForKey(state, { key }) || {}

        const newValue = formData[key]
        if (isArray) {
          const filteredNewValue = newValue.filter(
            ({ content } = {}) => !!content
          )
          return result.concat(
            filteredNewValue.length > 0
              ? filteredNewValue.map((individualValue, index) => {
                  return {
                    key,
                    id,
                    isArray: true,
                    isFileField: false,
                    mode: index === 0 ? 'REPLACE' : 'APPEND',
                    valueId,
                    value: individualValue,
                  }
                })
              : [
                  {
                    key,
                    id,
                    isArray: true,
                    isFileField: false,
                    mode: 'REPLACE',
                    valueId,
                    remove: true,
                  },
                ]
          )
        }
        const isFileField = type === 'FILE' && newValue instanceof File
        const bothEmpty = valueIsEmpty(newValue) && valueIsEmpty(existingValue)
        // If new and existing values are equivalent return early
        const equalValues = deepEqual(newValue, existingValue)
        const unchangedFile =
          isFileField && newValue.remove === 'false' && newValue.size === 0
        const isFileRemoved = newValue.remove === 'true'
        delete newValue.remove
        if ((equalValues || bothEmpty) && !isFileField) return result
        if (unchangedFile) return result
        const firstForKey = !doneKeys.includes(key)
        doneKeys.push(key)
        result.push({
          key,
          id,
          isFileField,
          firstForKey,
          valueId,
          value: isFileRemoved ? null : newValue,
          remove: isFileRemoved,
        })
        return result
      }, [])
      .filter(x => x !== null)
    if (newValues.length > 0) {
      dispatch(doUpdateCustomFieldValues(subjectId, newValues))
    }
  }
}

const changeContactMutation = `
mutation changeContactMutation($conversationId: ID!, $contactId: ID!) {
  conversationChangeContact(input: { conversationId: $conversationId, contactId: $contactId }) {
    conversation {
      id
      number
      contact {
        id
      }
    }
  }
}
`
export function doChangeContactForConversation(
  conversationId,
  conversationNumber,
  contactId
) {
  return dispatch => {
    const variables = {
      conversationId,
      contactId,
    }
    return dispatch(
      doAppGraphqlRequest(
        CHANGE_CONVERSATION_CONTACT,
        changeContactMutation,
        variables,
        {
          optimist: {
            conversationNumber,
            contactId,
          },
          meta: {
            mergeEntities: true,
          },
          normalizationSchema: changeContactMutationSchema,
        }
      )
    )
  }
}

const createContactMutation = `
mutation createContactMutation($customFields: [CustomFieldInput!]) {
  contactCreate(input: { contactType: contact, customFields: $customFields }) {
    contact {
      id
    }
  }
}

`
export function doCreateAndChangeContactForConversation(
  contactIdOrTemp, // could be a temp conv id, needed to mark isCreating flag
  conversationId,
  conversationNumber,
  name,
  email,
  contactMatrixId
) {
  return (dispatch, getState) => {
    const state = getState()
    const isInChat = selectIsInChat(state)
    const { id: emailId } = selectCustomFieldForKey(state, {
      key: 'contact_email',
    })
    const { id: firstNameId } = selectCustomFieldForKey(state, {
      key: 'contact_first_name',
    })
    const { id: lastNameId } = selectCustomFieldForKey(state, {
      key: 'contact_last_name',
    })
    const { id: contactMatrixFieldId } = selectCustomFieldForKey(state, {
      key: 'contact_matrix_id',
    })
    const [, firstName, lastName] = name.match(/^(.+) ([^ ]+)$/) || [null, name]
    return dispatch(
      doAppGraphqlRequest(
        CREATE_CONTACT,
        createContactMutation,
        {
          customFields: [
            email && { customFieldIdOrKey: emailId, value: email },
            firstName && { customFieldIdOrKey: firstNameId, value: firstName },
            lastName && { customFieldIdOrKey: lastNameId, value: lastName },
            contactMatrixId && {
              customFieldIdOrKey: contactMatrixFieldId,
              value: contactMatrixId,
            },
          ].filter(x => !!x),
        },
        {
          normalizationSchema: createContactMutationSchema,
          meta: {
            contactIdOrTemp,
          },
        }
      )
    )
      .then(result => {
        const { contactCreate: { contact: { id } = {} } = {} } = result || {}
        // in chat we fetch conversations by matrix id custom field, not by conversation id
        // so no need to link. API throws error otherwise
        if (!isInChat && id) {
          return dispatch(
            doChangeContactForConversation(
              conversationId,
              conversationNumber,
              id
            )
          )
        }
        return result
      })
      .then(result => {
        const changeContactId =
          result?.conversationChangeContact?.conversation?.contact?.id
        const createContactId = result?.contactCreate?.contact?.id

        const hasCreatedContactId = isInChat ? createContactId : changeContactId
        // reload contact page, only if the change and/or create contact executes successfully
        if (hasCreatedContactId) {
          return dispatch(doFetchCurrentContact())
        }

        return result
      })
  }
}
